import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardList, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";

export default function FormView() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [answers, setAnswers] = useState({});
  const [respondent, setRespondent] = useState({ email: "", name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/forms/${slug}`)
      .then((r) => { if (alive) setForm(r.data); })
      .catch(() => { if (alive) setNotFound(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  const setAnswer = (fid, value) => setAnswers((a) => ({ ...a, [fid]: value }));
  const toggleCheckbox = (fid, opt) => {
    const arr = Array.isArray(answers[fid]) ? answers[fid] : [];
    setAnswer(fid, arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post(`/forms/${slug}/submit`, {
        answers,
        respondent_email: respondent.email,
        respondent_name: respondent.name,
      });
      setSuccess(r.data?.message || form.success_message || "Merci !");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-neutral-500">
          <Loader2 className="mx-auto h-6 w-6 animate-spin" />
        </div>
      </PublicLayout>
    );
  }

  if (notFound || !form) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-3xl px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-navy">Formulaire introuvable</h1>
          <p className="mt-2 text-neutral-500">Ce formulaire n'existe plus ou n'est pas publié.</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-wine-700 hover:underline"><ArrowLeft className="h-4 w-4" /> Retour à l'accueil</Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="mx-auto max-w-3xl px-4 py-12">
        {/* Header */}
        <div className="rounded-3xl bg-gradient-to-br from-wine-500 to-wine-700 p-8 text-white shadow-xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-xs font-bold uppercase tracking-wider">
            <ClipboardList className="h-3.5 w-3.5" /> Formulaire
          </div>
          <h1 className="mt-4 text-3xl font-bold md:text-4xl" data-testid="form-title">{form.title}</h1>
          {form.description && <p className="mt-3 text-base text-white/90 whitespace-pre-line" data-testid="form-description">{form.description}</p>}
        </div>

        {success ? (
          <div className="mt-8 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="form-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
            <h2 className="mt-4 text-2xl font-bold text-navy">Merci !</h2>
            <p className="mt-2 text-neutral-700">{success}</p>
            <Link to="/" className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600" data-testid="form-back-home">
              <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5 rounded-2xl border border-neutral-100 bg-white p-6 shadow-lg" data-testid="form-public-form">
            {/* Respondent identity (optional but recommended) */}
            <div className="grid gap-3 md:grid-cols-2 rounded-xl bg-neutral-50 p-4">
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Votre nom (facultatif)</label>
                <input value={respondent.name} onChange={(e) => setRespondent({ ...respondent, name: e.target.value })} placeholder="Prénom Nom" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="form-respondent-name" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Votre email (facultatif)</label>
                <input type="email" value={respondent.email} onChange={(e) => setRespondent({ ...respondent, email: e.target.value })} placeholder="vous@exemple.com" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="form-respondent-email" />
              </div>
            </div>

            {/* Dynamic fields */}
            {(form.fields || []).map((f, idx) => (
              <div key={f.id || idx} className="rounded-xl border border-neutral-100 p-4" data-testid={`form-field-${idx}`}>
                <label className="block text-sm font-semibold text-navy">
                  {f.label} {f.required && <span className="text-rose-500">*</span>}
                </label>

                {f.type === "textarea" && (
                  <textarea required={f.required} rows={4} value={answers[f.id] || ""} onChange={(e) => setAnswer(f.id, e.target.value)} placeholder={f.placeholder || ""} className="mt-2 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid={`form-input-${idx}`} />
                )}

                {["text", "email", "number", "tel", "url", "date"].includes(f.type) && (
                  <input
                    type={f.type === "tel" ? "tel" : f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "url" ? "url" : f.type === "email" ? "email" : "text"}
                    required={f.required}
                    value={answers[f.id] || ""}
                    onChange={(e) => setAnswer(f.id, e.target.value)}
                    placeholder={f.placeholder || ""}
                    className="mt-2 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm"
                    data-testid={`form-input-${idx}`}
                  />
                )}

                {f.type === "select" && (
                  <select required={f.required} value={answers[f.id] || ""} onChange={(e) => setAnswer(f.id, e.target.value)} className="mt-2 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid={`form-input-${idx}`}>
                    <option value="">— Choisir —</option>
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                )}

                {f.type === "radio" && (
                  <div className="mt-2 space-y-1.5">
                    {(f.options || []).map((o, i) => (
                      <label key={o} className="flex cursor-pointer items-center gap-2 rounded-lg border border-neutral-100 px-3 py-2 hover:bg-wine-50">
                        <input type="radio" name={`fld-${f.id}`} required={f.required} checked={answers[f.id] === o} onChange={() => setAnswer(f.id, o)} className="accent-wine-500" data-testid={`form-radio-${idx}-${i}`} />
                        <span className="text-sm">{o}</span>
                      </label>
                    ))}
                  </div>
                )}

                {f.type === "checkbox" && (
                  <div className="mt-2 space-y-1.5">
                    {(f.options || []).map((o, i) => {
                      const arr = Array.isArray(answers[f.id]) ? answers[f.id] : [];
                      const checked = arr.includes(o);
                      return (
                        <label key={o} className="flex cursor-pointer items-center gap-2 rounded-lg border border-neutral-100 px-3 py-2 hover:bg-wine-50">
                          <input type="checkbox" checked={checked} onChange={() => toggleCheckbox(f.id, o)} className="accent-wine-500" data-testid={`form-check-${idx}-${i}`} />
                          <span className="text-sm">{o}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}

            <div className="flex items-center justify-end gap-2 border-t border-neutral-100 pt-4">
              <button type="submit" disabled={submitting} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-6 py-2.5 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="form-submit-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Envoyer ma réponse
              </button>
            </div>
          </form>
        )}
      </div>
    </PublicLayout>
  );
}
